import { api } from "./client";
import type { ApiResponse } from "./types";

export interface YashBalance {
  balance: number;
  totalEarned: number;
  totalSpent: number;
  trustMultiplier?: number;
}

export interface YashLedgerEntry {
  id: string;
  type: string;
  yash: number;
  balanceAfter: number;
  actionType: string | null;
  sourceType: string | null;
  sourceId: string | null;
  status: string;
  createdAt: string;
}

export interface YashPartner {
  id: string;
  businessName: string;
  category: string | null;
  city: string | null;
  state: string | null;
  logoUrl: string | null;
  isVerified?: boolean;
  offerCount?: number;
}

export interface YashRedemption {
  id: string;
  redemptionCode: string;
  yashSpent: number;
  status: "pending" | "active" | "used" | "expired" | string;
  issuedAt: string;
  expiresAt: string;
  usedAt: string | null;
  offer: { id: string; title: string; discountValue: string | null; discountType: string | null };
  partner: { id: string; businessName: string; address: string | null };
}

// GET /yash/balance — the controller wraps the balance object as `{ balance }`
// and names totals `lifetimeEarned`/`lifetimeSpent`. Normalize to the flat
// shape the page reads (balance / totalEarned / totalSpent).
export async function getBalance(): Promise<ApiResponse<YashBalance>> {
  const res = await api.get<
    ApiResponse<{
      balance: {
        balance: number;
        lifetimeEarned: number;
        lifetimeSpent: number;
        trustScore: number;
      };
    }>
  >("/yash/balance");
  const b = res.data.balance;
  return {
    ...res,
    data: {
      balance: b.balance,
      totalEarned: b.lifetimeEarned,
      totalSpent: b.lifetimeSpent,
      trustMultiplier: b.trustScore,
    },
  };
}

export function getHistory(params?: { page?: number; limit?: number }) {
  return api.get<ApiResponse<{ transactions: YashLedgerEntry[]; meta: { total: number } }>>(
    "/yash/history",
    params as Record<string, string | number | boolean | undefined>,
  );
}

export function getPartners() {
  return api.get<ApiResponse<{ partners: YashPartner[] }>>("/yash/partners");
}

export function getMyRedemptions() {
  return api.get<ApiResponse<{ redemptions: YashRedemption[]; meta: { total: number } }>>(
    "/yash/redemptions/me",
  );
}

export interface YashOffer {
  id: string;
  title: string;
  description: string | null;
  yashRequired: number;
  discountValue: string | null;
  discountType: string | null;
  maxRedemptions: number | null;
  currentRedemptions: number;
  validFrom: string;
  validUntil: string | null;
  isAvailable: boolean;
}

// GET /yash/partners/:id/offers returns the partner (with offers nested) under
// a `partner` key.
export function getPartnerOffers(partnerId: string) {
  return api.get<
    ApiResponse<{ partner: { id: string; businessName: string; offers: YashOffer[] } }>
  >(`/yash/partners/${partnerId}/offers`);
}

export interface RedeemResult {
  id: string;
  redemptionCode: string;
  expiresAt: string;
}

export function redeemOffer(offerId: string) {
  return api.post<ApiResponse<RedeemResult>>("/yash/redeem", { offerId });
}
